import React, { useState } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import { Checkbox } from "../../../components/ui/Checkbox";
import TenantDetailsModal from "./TenantDetailsModal";

interface TenantTableProps {
	tenants: any[];
	selectedTenants: string[];
	onSelectTenant: (tenantId: string) => void;
	onSelectAll: () => void;
	onEdit?: (tenant: any) => void;
	onDelete?: (tenantId: string, tenantName: string) => void;
}

const TenantTable: React.FC<TenantTableProps> = ({
	tenants,
	selectedTenants,
	onSelectTenant,
	onSelectAll,
	onEdit,
	onDelete,
}) => {
	const [sortField, setSortField] = useState<string>("name");
	const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
	const [expandedRow, setExpandedRow] = useState<string | null>(null);
	const [selectedTenantDetails, setSelectedTenantDetails] = useState<
		any | null
	>(null);

	const handleSort = (field: string) => {
		if (sortField === field) {
			setSortDirection(sortDirection === "asc" ? "desc" : "asc");
		} else {
			setSortField(field);
			setSortDirection("asc");
		}
	};

	const getStatusBadge = (status: string) => {
		const statusConfig: Record<string, { color: string; label: string }> = {
			active: { color: "bg-success/10 text-success", label: "Active" },
			inactive: { color: "bg-muted text-muted-foreground", label: "Inactive" },
			pending: { color: "bg-warning/10 text-warning", label: "Pending" },
			terminated: { color: "bg-error/10 text-error", label: "Terminated" },
		};

		const config = statusConfig[status] || statusConfig.inactive;
		return (
			<span
				className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}
			>
				{config.label}
			</span>
		);
	};

	const getPaymentStatusBadge = (status: string) => {
		const statusConfig: Record<string, { color: string; label: string }> = {
			current: { color: "bg-success/10 text-success", label: "Current" },
			overdue: { color: "bg-error/10 text-error", label: "Overdue" },
			partial: { color: "bg-warning/10 text-warning", label: "Partial" },
			pending: { color: "bg-primary/10 text-primary", label: "Pending" },
		};

		const config = statusConfig[status] || statusConfig.pending;
		return (
			<span
				className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}
			>
				{config.label}
			</span>
		);
	};

	const handleRowExpand = (tenantId: string) => {
		setExpandedRow(expandedRow === tenantId ? null : tenantId);
	};

	const handleViewDetails = (tenant: any) => {
		setSelectedTenantDetails(tenant);
	};

	const formatDate = (dateString: string | null | undefined): string => {
		if (!dateString) return "N/A";
		try {
			const date = new Date(dateString);
			if (isNaN(date.getTime())) return "N/A";
			return date.toLocaleDateString("en-US", {
				year: "numeric",
				month: "short",
				day: "numeric",
			});
		} catch {
			return "N/A";
		}
	};

	const sortedTenants = [...tenants].sort((a, b) => {
		const aValue = a[sortField];
		const bValue = b[sortField];

		if (sortDirection === "asc") {
			return aValue > bValue ? 1 : -1;
		} else {
			return aValue < bValue ? 1 : -1;
		}
	});

	return (
		<>
			<div className="bg-card border border-border rounded-lg overflow-hidden">
				{/* Table with horizontal scroll on mobile */}
				<div className="overflow-x-auto -mx-6 sm:mx-0">
					<div className="inline-block min-w-full align-middle sm:px-0">
						<table className="min-w-full divide-y divide-border">
							<thead className="bg-muted/50 border-b border-border">
								<tr>
									<th className="p-4 text-left">
										<Checkbox
											checked={selectedTenants.length === tenants.length}
											onChange={onSelectAll}
										/>
									</th>
									<th
										className="p-4 text-left cursor-pointer hover:bg-muted/70 smooth-transition"
										onClick={() => handleSort("name")}
									>
										<div className="flex items-center space-x-2">
											<span className="text-sm font-medium text-foreground">
												Tenant
											</span>
											<Icon
												name={
													sortField === "name" && sortDirection === "desc"
														? "ChevronDown"
														: "ChevronUp"
												}
												size={16}
											/>
										</div>
									</th>
									<th
										className="p-4 text-left cursor-pointer hover:bg-muted/70 smooth-transition"
										onClick={() => handleSort("property")}
									>
										<div className="flex items-center space-x-2">
											<span className="text-sm font-medium text-foreground">
												Property
											</span>
											<Icon
												name={
													sortField === "property" && sortDirection === "desc"
														? "ChevronDown"
														: "ChevronUp"
												}
												size={16}
											/>
										</div>
									</th>
									<th className="p-4 text-left">
										<span className="text-sm font-medium text-foreground">
											Unit
										</span>
									</th>
									<th
										className="p-4 text-left cursor-pointer hover:bg-muted/70 smooth-transition"
										onClick={() => handleSort("leaseEnd")}
									>
										<div className="flex items-center space-x-2">
											<span className="text-sm font-medium text-foreground">
												Lease End
											</span>
											<Icon
												name={
													sortField === "leaseEnd" && sortDirection === "desc"
														? "ChevronDown"
														: "ChevronUp"
												}
												size={16}
											/>
										</div>
									</th>
									<th className="p-4 text-left">
										<span className="text-sm font-medium text-foreground">
											Status
										</span>
									</th>
									<th className="p-4 text-left">
										<span className="text-sm font-medium text-foreground">
											Payment
										</span>
									</th>
									<th className="p-4 text-left">
										<span className="text-sm font-medium text-foreground">
											Contact
										</span>
									</th>
									<th className="p-4 text-left">
										<span className="text-sm font-medium text-foreground">
											Actions
										</span>
									</th>
								</tr>
							</thead>
							<tbody>
								{sortedTenants.map((tenant) => (
									<React.Fragment key={tenant.id}>
										<tr className="border-b border-border hover:bg-muted/30 smooth-transition">
											<td className="p-4">
												<Checkbox
													checked={selectedTenants.includes(tenant.id)}
													onChange={() => onSelectTenant(tenant.id)}
												/>
											</td>
											<td className="p-4">
												<div className="flex items-center space-x-3">
													<div className="w-10 h-10 bg-primary/10 text-primary rounded-full flex items-center justify-center">
														<Icon name="User" size={20} />
													</div>
													<div>
														<div className="flex items-center space-x-2">
															<p className="font-medium text-foreground">
																{tenant.name}
															</p>
															<span className="px-2 py-0.5 text-xs font-medium rounded-full bg-primary/10 text-primary">
																{tenant.property_owner
																	? "Property Owner"
																	: "Tenant"}
															</span>
														</div>
														<p className="text-sm text-muted-foreground">
															{tenant.email}
														</p>
													</div>
												</div>
											</td>
											<td className="p-4">
												<p className="text-sm text-foreground">
													{tenant.property || "N/A"}
												</p>
											</td>
											<td className="p-4">
												<p className="text-sm text-foreground">{tenant.unit}</p>
											</td>
											<td className="p-4">
												<p className="text-sm text-foreground">
													{formatDate(tenant.leaseEnd)}
												</p>
											</td>
											<td className="p-4">{getStatusBadge(tenant.status)}</td>
											<td className="p-4">
												{getPaymentStatusBadge(tenant.paymentStatus)}
											</td>
											<td className="p-4">
												<div className="flex items-center space-x-2">
													<Button
														variant="ghost"
														size="icon"
														onClick={() => window.open(`tel:${tenant.phone}`)}
													>
														<Icon name="Phone" size={16} />
													</Button>
													<Button
														variant="ghost"
														size="icon"
														onClick={() =>
															window.open(`mailto:${tenant.email}`)
														}
													>
														<Icon name="Mail" size={16} />
													</Button>
												</div>
											</td>
											<td className="p-4">
												<div className="flex items-center space-x-2">
													<Button
														variant="ghost"
														size="icon"
														onClick={() => handleRowExpand(tenant.id)}
													>
														<Icon
															name={
																expandedRow === tenant.id
																	? "ChevronUp"
																	: "ChevronDown"
															}
															size={16}
														/>
													</Button>
													<Button
														variant="ghost"
														size="icon"
														onClick={() => handleViewDetails(tenant)}
														title="View Details"
													>
														<Icon name="Eye" size={16} />
													</Button>
													{onEdit && (
														<Button
															variant="ghost"
															size="icon"
															onClick={() => onEdit(tenant)}
															title="Edit Tenant"
														>
															<Icon name="Edit" size={16} />
														</Button>
													)}
													{onDelete && (
														<Button
															variant="ghost"
															size="icon"
															onClick={() => onDelete(tenant.id, tenant.name)}
															title="Delete Tenant"
														>
															<Icon name="Trash2" size={16} />
														</Button>
													)}
												</div>
											</td>
										</tr>

										{expandedRow === tenant.id && (
											<tr className="bg-muted/20">
												<td colSpan={9} className="p-6">
													<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
														<div>
															<h4 className="font-medium text-foreground mb-3">
																Lease Details
															</h4>
															<div className="space-y-2 text-sm">
																<p>
																	<span className="text-muted-foreground">
																		Start Date:
																	</span>{" "}
																	{formatDate(tenant.leaseStart)}
																</p>
																<p>
																	<span className="text-muted-foreground">
																		End Date:
																	</span>{" "}
																	{formatDate(tenant.leaseEnd)}
																</p>
																<p>
																	<span className="text-muted-foreground">
																		Monthly Rent:
																	</span>{" "}
																	{tenant.monthlyRent
																		? new Intl.NumberFormat("en-NG", {
																				style: "currency",
																				currency: "NGN",
																				minimumFractionDigits: 0,
																		  }).format(Number(tenant.monthlyRent) || 0)
																		: "N/A"}
																</p>
																<p>
																	<span className="text-muted-foreground">
																		Status:
																	</span>{" "}
																	<span className="capitalize">
																		{tenant.status || "N/A"}
																	</span>
																</p>
															</div>
														</div>

														<div>
															<h4 className="font-medium text-foreground mb-3">
																Contact Information
															</h4>
															<div className="space-y-2 text-sm">
																<p>
																	<span className="text-muted-foreground">
																		Email:
																	</span>{" "}
																	{tenant.email || "N/A"}
																</p>
																<p>
																	<span className="text-muted-foreground">
																		Phone:
																	</span>{" "}
																	{tenant.phone || "N/A"}
																</p>
																<p>
																	<span className="text-muted-foreground">
																		Property:
																	</span>{" "}
																	{tenant.property || "N/A"}
																</p>
																<p>
																	<span className="text-muted-foreground">
																		Unit:
																	</span>{" "}
																	{tenant.unit || "N/A"}
																</p>
															</div>
														</div>

														<div>
															<h4 className="font-medium text-foreground mb-3">
																Payment Status
															</h4>
															<div className="space-y-2 text-sm">
																<p>
																	<span className="text-muted-foreground">
																		Status:
																	</span>{" "}
																	{getPaymentStatusBadge(tenant.paymentStatus)}
																</p>
																<p>
																	<span className="text-muted-foreground">
																		Lease Status:
																	</span>{" "}
																	{getStatusBadge(tenant.status)}
																</p>
																<div className="pt-2">
																	<Button
																		variant="outline"
																		size="sm"
																		onClick={() => handleViewDetails(tenant)}
																		iconName="Eye"
																		iconPosition="left"
																		fullWidth
																	>
																		View Full Details
																	</Button>
																</div>
															</div>
														</div>
													</div>
												</td>
											</tr>
										)}
									</React.Fragment>
								))}
							</tbody>
						</table>
					</div>
				</div>
			</div>

			{selectedTenantDetails && (
				<TenantDetailsModal
					tenant={selectedTenantDetails}
					onClose={() => setSelectedTenantDetails(null)}
				/>
			)}
		</>
	);
};

export default TenantTable;
